import React, { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { SlidePanel, FormInput, FormTextarea, Alert } from 'ui/patterns';
import { Button, Stack, Checkbox } from 'ui/components';

import { useSaveCustomRecords, useUpdateCustomRecords } from 'hooks';

import * as Icon from 'assets/icons';

import { showErrorToast, showSuccessToast } from 'utils/showToast';

import { CustomRecords } from 'types/customTables';
import { CustomRecordProps } from './addEditCustomRecordTypes';
import { ErrorModal } from 'types/modalTypes';

const formDefaults = {
  EmailTo: '',
  Values: [],
  AccessType: false,
  AccessTypeId: 0,
  HasAccess: false,
  AccountId: 0,
  Id: '0',
  TableId: 0,
};

export default function AddEditCustomRecords({
  showPanel,
  togglePanel,
  record,
  selectedTable,
  accountId,
  tableId,
  mode,
}: CustomRecordProps): ReactElement {
  const customColumns = selectedTable?.Columns;
  const { mutate: saveCustomRecords, isLoading: isSaveRecordsLoading } =
    useSaveCustomRecords();
  const { mutate: updateCustomRecords, isLoading: isUpdateRecordsLoading } =
    useUpdateCustomRecords();

  const { register, handleSubmit, reset } = useForm<Partial<CustomRecords>>({
    defaultValues: { ...formDefaults },
  });
  const selectedRecord = record?.original;
  const recordId = record?.original?._id;

  const removeSpacesFromKey = (obj: any, targetKey: string) => {
    if (obj && typeof obj === 'object') {
      Object.keys(obj).forEach(key => {
        if (key.toString() === targetKey) {
          const newKey = key.replace(/\s+/g, ''); // Remove all spaces from the key
          obj[newKey] = obj[key];
          delete obj[key];
        }
      });
    }
    return obj;
  };
  const cleanedRecord = removeSpacesFromKey(selectedRecord, 'Email To');

  useEffect(() => {
    if (selectedRecord && (!isSaveRecordsLoading || !isUpdateRecordsLoading)) {
      const { AccessTypeId, EmailTo, HasAccess, ...customLookupRecordValues } =
        cleanedRecord;
      delete customLookupRecordValues.CustomLookupId;
      delete customLookupRecordValues._id;
      delete customLookupRecordValues.LastUpdatedBy;
      delete customLookupRecordValues.LastUpdatedOn;
      reset({
        EmailTo: EmailTo,
        AccessType: parseInt(AccessTypeId) === 1 ? true : false,
        HasAccess: HasAccess,
        Id: recordId,
        ...customLookupRecordValues,
      });
    }
  }, [
    selectedRecord,
    reset,
    isSaveRecordsLoading,
    isUpdateRecordsLoading,
    recordId,
  ]);

  function resetForm(): void {
    const values = [...customColumns.values()];
    const columnNames: Record<string, string> = {};
    for (const value of values) {
      columnNames[value] = '';
    }
    reset({
      ...columnNames,
      EmailTo: '',
      AccessTypeId: 0,
      AccessType: false,
    });
  }

  function onSubmit(
    data: {
      EmailTo: string;
      AccessType: boolean;
    } & {
      [key: string]: string | number;
    },
  ) {
    let result = Object.keys(data).filter(key => {
      if (key !== 'AccessType' && key !== 'EmailTo') return data[key];
      return undefined;
    });
    result = result.filter(item => item !== undefined);
    const { AccessType, EmailTo, ...CustomLookupRecordValues } = data;
    if (result.length > 0) {
      if (recordId === undefined) {
        saveCustomRecords(
          {
            params: {
              EmailTo: EmailTo,
              AccessTypeId: AccessType === true ? 1 : 0,
              HasAccess: AccessType,
              CustomLookupRecordValues: CustomLookupRecordValues,
              LastUpdated: selectedTable.LastUpdated,
              LastUpdatedBy: selectedTable.LastUpdatedBy,
              CustomLookupId: tableId.toString(),
            },
            accountId: accountId,
            lookupId: tableId.toString(),
          },
          {
            onSuccess: () => {
              showSuccessToast({
                message: 'Your changes were successfully saved.',
              });
              togglePanel();
              resetForm();
            },
            onError: (error: ErrorModal) => {
              showErrorToast({ message: error.Errors[0].Message });
            },
          },
        );
      } else {
        updateCustomRecords(
          {
            params: {
              EmailTo: EmailTo,
              AccessTypeId: AccessType === true ? 1 : 0,
              HasAccess: AccessType,
              CustomLookupRecordValues: CustomLookupRecordValues,
              LastUpdated: selectedTable.LastUpdated,
              LastUpdatedBy: selectedTable.LastUpdatedBy,
              CustomLookupId: tableId.toString(),
              _id: recordId,
            },
            accountId: accountId,
            lookupId: tableId.toString(),
            recordId: recordId,
          },
          {
            onSuccess: () => {
              showSuccessToast({
                message: 'Your changes were successfully saved.',
              });
              togglePanel();
              resetForm();
            },
            onError: (error: ErrorModal) => {
              showErrorToast({ message: error.Errors[0].Message });
            },
          },
        );
      }
    } else
      showErrorToast({
        message: 'Please enter atleast one column value to continue.',
      });
  }

  const addRecordsAction = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          resetForm();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit(onSubmit)}
        disabled={isSaveRecordsLoading || isUpdateRecordsLoading}
      >
        Save
      </Button>
    </>
  );

  return (
    <>
      <SlidePanel
        title={`${mode === 'Add' ? 'Add Record' : 'Edit Record'}`}
        actions={addRecordsAction()}
        onClose={() => {
          togglePanel();
          resetForm();
        }}
        open={showPanel}
      >
        <Stack direction="y" gap={4}>
          {customColumns &&
            customColumns.map((item: string) => (
              <FormTextarea
                key={item}
                label={item}
                rows={1}
                register={register}
                name={item}
              />
            ))}

          <Alert variant="info" icon={<Icon.Info />}>
            To specify more than one email, separate each email with a comma.
          </Alert>

          <FormInput
            label="Email To"
            type="email"
            register={register}
            name="EmailTo"
          />
          <Checkbox
            label="Limited Access"
            register={register}
            name="AccessType"
          />
        </Stack>
      </SlidePanel>
    </>
  );
}
